import { useId } from 'react';
import { Link } from 'react-router-dom';
import type { DecisaoDaDivergencia } from '../../api/aberturas.js';
import { usePode } from '../../autorizacao/usePode.js';
import { Botao } from '../../componentes/Botao.js';
import { formatarDataHora } from '../../componentes/datas.js';
import { rotuloDoLocal } from '../../componentes/deposito.js';
import { IconeAtencao } from '../../componentes/Icones.js';
import { rotuloDaDecisao } from './DialogoDeDecisao.js';
import type { AberturaEmDivergencia } from './DialogoDeDecisao.js';
import { entregueAntesDaRetida } from './quantidade.js';
import { caminhoDaAbertura } from './recorte.js';
import { Saldo } from './SaldoDaAbertura.js';
import './CartaoDeDivergencia.css';

type Props = {
  readonly abertura: AberturaEmDivergencia;
  readonly aoEscolher: (decisao: DecisaoDaDivergencia) => void;
  /** No detalhe, o link para ele mesmo é ruído. */
  readonly comLink?: boolean;
};

/**
 * Uma divergência de quantidade esperando decisão (r1-mov, passo 2.2).
 *
 * Tudo o que a decisão pede está no cartão: quanto foi iniciado, quanto já
 * tinha sido entregue, quanto o operador informou na entrega que passou, o
 * excedente, quem foi e para onde. As ações só existem para quem pode decidir
 * (Administrador e Supervisor, `operacoes.agir`); o perfil Consulta lê.
 */
export function CartaoDeDivergencia({ abertura, aoEscolher, comLink = true }: Props) {
  const pode = usePode();
  const idTitulo = useId();
  const { retida } = abertura;
  const entregueAntes = entregueAntesDaRetida({
    quantidade: abertura.quantidade,
    entregue: abertura.entregue,
    retida: retida.quantidade,
  });

  return (
    <article className="divergencia" aria-labelledby={idTitulo}>
      <header className="divergencia__cabecalho">
        <span className="divergencia__icone">
          <IconeAtencao tamanho={18} />
        </span>
        <h3 className="divergencia__titulo" id={idTitulo}>
          <span className="mono">{abertura.produto}</span>
          {abertura.derivacao !== null && (
            <span className="divergencia__derivacao">{abertura.derivacao}</span>
          )}
        </h3>
        {comLink && (
          <Link className="divergencia__link" to={caminhoDaAbertura(abertura.id)}>
            Ver a movimentação
          </Link>
        )}
      </header>

      <Saldo
        itens={[
          { rotulo: 'Iniciada', valor: abertura.quantidade },
          { rotulo: 'Já entregue antes', valor: entregueAntes ?? '—' },
          { rotulo: 'Informada na entrega', valor: retida.quantidade, destaque: true },
          { rotulo: 'Excedente', valor: abertura.excedente, destaque: true },
        ]}
      />

      <dl className="divergencia__fatos">
        <div>
          <dt>Trajeto</dt>
          <dd className="mono">
            {rotuloDoLocal(abertura.filialOrigem, abertura.depositoOrigem)} →{' '}
            {rotuloDoLocal(retida.filialDestino, retida.depositoDestino)}
          </dd>
        </div>
        <div>
          {/* A entrega retida não traz quem a fez: o nome aqui é de quem iniciou. */}
          <dt>Iniciada por</dt>
          <dd>
            {abertura.operador.nome} · {abertura.operador.matricula}
          </dd>
        </div>
        <div>
          <dt>Entrega informada em</dt>
          <dd>{formatarDataHora(retida.registradoEm)}</dd>
        </div>
      </dl>

      <footer className="divergencia__rodape">
        {pode('operacoes.agir') ? (
          (['Manter', 'Atualizar'] as const).map((decisao) => (
            <Botao
              key={decisao}
              variante={decisao === 'Manter' ? 'secundario' : 'atencao'}
              altura="pequena"
              onClick={() => {
                aoEscolher(decisao);
              }}
            >
              {rotuloDaDecisao(abertura, decisao)}
            </Botao>
          ))
        ) : (
          <span className="divergencia__nota">Decisão de Administrador ou Supervisor.</span>
        )}
      </footer>
    </article>
  );
}
